/**
 * Deterministic normalization of the pre-canonical generation datasets into the
 * canonical equipment-library schema, plus the immutable `equipment_legacy_ids`
 * bridge.
 *
 * Sources normalized here (all facts-only projections of existing cited data —
 * no value is invented):
 *   - 30 OEM prime movers (`OEM_GENERATION_SOURCE_REGISTER.products`)
 *   - 3 alternators (`OEM_GENERATION_SOURCE_REGISTER.electricalMachines`)
 *   - 3 reactor designs (`REACTOR_VENDOR_CATALOG`)
 *   - 3 immutable Generation & Impact v1 envelopes (`GENERATION_EQUIPMENT_CATALOG`)
 *
 * The three v1 envelopes are preserved as permanent `generation-equipment-catalog-v1`
 * admissions with an exact, digest-frozen compatibility snapshot so the v1
 * numerical solver inputs can never drift unnoticed (see the parity test).
 */
import type {
  EquipmentAdmission,
  EquipmentDomain,
  EquipmentFacets,
  EquipmentKind,
  EquipmentLibraryBundle,
  EquipmentLibraryProduct,
  EquipmentLifecycleStatus,
  EquipmentObservation,
  EquipmentObservationBasis,
  EquipmentRevision,
  EquipmentSource,
  EquipmentSourceKind,
  EquipmentTechnology,
  LegacyEquipmentIdLink,
} from '../../types/equipmentLibrary';
import { EQUIPMENT_LIBRARY_SCHEMA_VERSION } from '../../types/equipmentLibrary';
import {
  sealEquipmentAdmission,
  sealEquipmentLibraryBundle,
  sealEquipmentObservation,
  sealEquipmentProduct,
  sealEquipmentRevision,
  sealEquipmentSource,
  sealLegacyEquipmentIdLink,
} from '../../lib/equipmentLibrary/digests';
import { OEM_GENERATION_SOURCE_REGISTER } from '../../lib/generationImpact/generationOemSourceRegister';
import { GENERATION_EQUIPMENT_CATALOG } from '../../lib/generationImpact/generationEquipmentCatalog';
import { REACTOR_VENDOR_CATALOG } from '../../config/reactorVendorCatalog';
import { hashCanonicalJson } from '../../lib/reportGeneration/reportContractHash';
import {
  buildLegacyPlanningBenchmarks,
  legacyPlanningBaselineSource,
} from './classifyLegacyCatalogRows';
import type {
  OemGenerationProduct,
  OemGenerationSource,
  OemElectricalMachineProduct,
} from '../../types/oemGenerationCatalog';

const CURATOR_ID = 'deep-atomic-equipment-curation';
const COMPILED_AT = OEM_GENERATION_SOURCE_REGISTER.compiledAt;
const REVISION_LABEL = 'legacy-normalized-2026q3';

// ---------------------------------------------------------------------------
// Deterministic enum mappings (prime-mover accurate; no plant cycle inferred).
// ---------------------------------------------------------------------------

function oemKind(technology: OemGenerationProduct['technology']): EquipmentKind {
  switch (technology) {
    case 'industrial-gas-turbine':
    case 'aeroderivative-gas-turbine':
      return 'gas-turbine';
    case 'microturbine':
      return 'microturbine';
    case 'spark-ignited-gas-engine':
    case 'compression-ignition-engine':
    case 'dual-fuel-engine':
      return 'reciprocating-engine';
  }
}

function oemTechnology(
  technology: OemGenerationProduct['technology'],
): EquipmentTechnology {
  switch (technology) {
    case 'industrial-gas-turbine':
    case 'aeroderivative-gas-turbine':
    case 'microturbine':
      return 'natural-gas-simple-cycle';
    case 'spark-ignited-gas-engine':
      return 'reciprocating-gas';
    case 'compression-ignition-engine':
      return 'reciprocating-liquid-fuel';
    case 'dual-fuel-engine':
      return 'dual-fuel-generation';
  }
}

function oemSourceKind(kind: OemGenerationSource['kind']): EquipmentSourceKind {
  switch (kind) {
    case 'product-page':
      return 'product-page';
    case 'datasheet':
      return 'datasheet';
    case 'product-guide':
    case 'portfolio':
      return 'product-guide';
    case 'sizing-tool':
    case 'application-manual':
      return 'application-manual';
  }
}

function emptyFacets(overrides: Partial<EquipmentFacets>): EquipmentFacets {
  return {
    technologies: [],
    energySources: [],
    fuels: [],
    primeMovers: [],
    cycles: [],
    coolingMethods: [],
    gridRoles: [],
    applications: [],
    regions: [],
    ...overrides,
  };
}

function singleFrequency(
  frequencyHz: readonly (50 | 60)[],
): { frequencyHz?: 50 | 60 } {
  return frequencyHz.length === 1 ? { frequencyHz: frequencyHz[0] } : {};
}

// ---------------------------------------------------------------------------
// Source compilation (all real OEM citations; nothing synthetic).
// ---------------------------------------------------------------------------

function compileOemSource(source: OemGenerationSource): EquipmentSource {
  return sealEquipmentSource({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: source.id,
    kind: oemSourceKind(source.kind),
    publisher: source.publisher,
    title: source.title,
    retrievedAt: source.retrievedAt,
    access: source.access === 'public' ? 'public' : 'registration-required',
    visibility: 'public',
    url: source.url,
    notes: source.notes,
  });
}

// ---------------------------------------------------------------------------
// OEM prime movers (30).
// ---------------------------------------------------------------------------

const oemRevisionId = (productId: string): string => `${productId}:${REVISION_LABEL}`;
const oemIdentityObsId = (productId: string): string => `${productId}:identity`;
const oemOutputObsId = (productId: string, caseId: string): string =>
  `${productId}:electrical-output:${caseId}`;

function compileOemProduct(product: OemGenerationProduct): EquipmentLibraryProduct {
  return sealEquipmentProduct({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: product.id,
    level: 'packaged-system',
    domain: 'energy-conversion',
    kind: oemKind(product.technology),
    manufacturer: product.manufacturer,
    family: product.variant || product.model,
    model: product.model,
    displayName: `${product.manufacturer} ${product.model}`,
    lifecycleStatus: 'commercial-current',
    tags: ['oem-generation-register', product.technology],
    facets: emptyFacets({
      technologies: [oemTechnology(product.technology)],
      energySources: ['natural-gas'],
      primeMovers: [oemKind(product.technology)],
    }),
    currentRevisionId: oemRevisionId(product.id),
    visibility: 'platform',
    origin: 'oem-publication',
    createdAt: COMPILED_AT,
    updatedAt: COMPILED_AT,
  }) as EquipmentLibraryProduct;
}

function compileOemObservations(
  product: OemGenerationProduct,
): EquipmentObservation[] {
  const revisionId = oemRevisionId(product.id);
  const identity = sealEquipmentObservation({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: oemIdentityObsId(product.id),
    revisionId,
    metric: 'custom:documented-product-identity',
    value: { kind: 'text', value: `${product.manufacturer} ${product.model}` },
    unit: '1',
    basis: { qualifier: 'nominal', configuration: product.variant || product.model },
    sourceRefs: product.identitySourceIds.map(sourceId => ({
      sourceId,
      locator: 'OEM product identity',
    })),
    evidenceStatus: 'published',
    confidence: 'high',
    reviewedAt: COMPILED_AT,
    reviewedBy: CURATOR_ID,
  });

  const preferred = product.performanceCases[0];
  if (!preferred) return [identity];

  const basis: EquipmentObservationBasis = {
    qualifier: preferred.duty === 'iso-rating' ? 'nominal' : 'typical',
    outputBoundary: preferred.ratingScope,
    duty: preferred.duty,
    fuel: preferred.fuel,
    ...singleFrequency(preferred.frequencyHz),
    referenceConditions: preferred.referenceConditions,
  };
  const output = sealEquipmentObservation({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: oemOutputObsId(product.id, preferred.id),
    revisionId,
    metric: 'electrical-output',
    value: { kind: 'scalar', value: preferred.electricalOutputKw },
    unit: 'kW',
    basis,
    sourceRefs: preferred.sourceIds.map(sourceId => ({
      sourceId,
      locator: `Performance case ${preferred.label}`,
    })),
    evidenceStatus: 'published',
    confidence: 'high',
    reviewedAt: COMPILED_AT,
    reviewedBy: CURATOR_ID,
  });
  return [identity, output];
}

function compileOemRevision(product: OemGenerationProduct): EquipmentRevision {
  const observations = compileOemObservations(product);
  const preferred = product.performanceCases[0];
  const sourceIds = [
    ...new Set([
      ...product.identitySourceIds,
      ...(preferred?.sourceIds ?? []),
    ]),
  ];
  return sealEquipmentRevision({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: oemRevisionId(product.id),
    productId: product.id,
    revisionLabel: REVISION_LABEL,
    variantLabel: product.variant || product.model,
    status: 'reviewed',
    configurationLevel: 'packaged-system',
    frequencyHz: preferred?.frequencyHz ?? [],
    fuels: preferred ? [preferred.fuel] : [],
    duties: preferred ? [preferred.duty] : [],
    sourceIds,
    observationIds: observations.map(observation => observation.id),
    compiledAt: COMPILED_AT,
    reviewedAt: COMPILED_AT,
    reviewedBy: CURATOR_ID,
    notes: product.dataGaps.length > 0
      ? `Known gaps: ${product.dataGaps.join('; ')}`
      : undefined,
  });
}

function compileOemAdmission(product: OemGenerationProduct): EquipmentAdmission {
  return sealEquipmentAdmission({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: `${product.id}:browse`,
    revisionId: oemRevisionId(product.id),
    surface: 'browse',
    status: 'admitted',
    requiredObservationIds: [oemIdentityObsId(product.id)],
    missingMetrics: [],
    conditions: ['OEM technical evidence; project price and lead time require a quote.'],
    reason: 'Manually reviewed OEM prime-mover identity and rating case for library visibility.',
    reviewedAt: COMPILED_AT,
    reviewedBy: CURATOR_ID,
  });
}

// ---------------------------------------------------------------------------
// Alternators (3).
// ---------------------------------------------------------------------------

const machineRevisionId = (id: string): string => `${id}:${REVISION_LABEL}`;
const machineIdentityObsId = (id: string): string => `${id}:identity`;
const machineRatingObsId = (id: string, caseId: string): string =>
  `${id}:rated-kva:${caseId}`;

function compileMachine(
  machine: OemElectricalMachineProduct,
): {
  product: EquipmentLibraryProduct;
  revision: EquipmentRevision;
  observations: EquipmentObservation[];
  admission: EquipmentAdmission;
} {
  const preferred =
    machine.ratingCases.find(item => item.id === machine.preferredRatingCaseId)
    ?? machine.ratingCases[0];
  const lifecycle: EquipmentLifecycleStatus =
    machine.productStatus === 'current'
      ? 'commercial-current'
      : machine.productStatus === 'legacy-current-support'
        ? 'commercial-legacy'
        : 'status-unconfirmed';

  const product = sealEquipmentProduct({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: machine.id,
    level: 'component',
    domain: 'electrical-balance-of-plant',
    kind: 'synchronous-generator',
    manufacturer: machine.manufacturer,
    family: machine.family,
    model: machine.model,
    displayName: `${machine.manufacturer} ${machine.model}`,
    lifecycleStatus: lifecycle,
    tags: ['oem-electrical-machine'],
    facets: emptyFacets({ gridRoles: ['synchronous-generation'] }),
    currentRevisionId: machineRevisionId(machine.id),
    visibility: 'platform',
    origin: 'oem-publication',
    createdAt: COMPILED_AT,
    updatedAt: COMPILED_AT,
  }) as EquipmentLibraryProduct;

  const identity = sealEquipmentObservation({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: machineIdentityObsId(machine.id),
    revisionId: machineRevisionId(machine.id),
    metric: 'custom:documented-product-identity',
    value: { kind: 'text', value: `${machine.manufacturer} ${machine.model}` },
    unit: '1',
    basis: { qualifier: 'nominal', configuration: machine.variant || machine.model },
    sourceRefs: machine.identitySourceIds.map(sourceId => ({
      sourceId,
      locator: 'Alternator identity',
    })),
    evidenceStatus: 'published',
    confidence: 'high',
    reviewedAt: COMPILED_AT,
    reviewedBy: CURATOR_ID,
  });
  const observations: EquipmentObservation[] = [identity];
  if (preferred) {
    observations.push(sealEquipmentObservation({
      schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
      id: machineRatingObsId(machine.id, preferred.id),
      revisionId: machineRevisionId(machine.id),
      metric: 'rated-kva',
      value: { kind: 'scalar', value: preferred.ratedKva },
      unit: 'kVA',
      basis: {
        qualifier: 'nominal',
        frequencyHz: preferred.frequencyHz,
        configuration: preferred.label,
      },
      sourceRefs: preferred.sourceIds.map(sourceId => ({
        sourceId,
        locator: `Rating case ${preferred.label}`,
      })),
      evidenceStatus: 'published',
      confidence: 'high',
      reviewedAt: COMPILED_AT,
      reviewedBy: CURATOR_ID,
    }));
  }

  const sourceIds = [
    ...new Set([...machine.identitySourceIds, ...(preferred?.sourceIds ?? [])]),
  ];
  const revision = sealEquipmentRevision({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: machineRevisionId(machine.id),
    productId: machine.id,
    revisionLabel: REVISION_LABEL,
    variantLabel: machine.variant || machine.model,
    status: 'reviewed',
    configurationLevel: 'component',
    frequencyHz: preferred ? [preferred.frequencyHz] : [],
    fuels: [],
    duties: [],
    sourceIds,
    observationIds: observations.map(observation => observation.id),
    compiledAt: COMPILED_AT,
    reviewedAt: COMPILED_AT,
    reviewedBy: CURATOR_ID,
    notes: machine.dataGaps.length > 0
      ? `Known gaps: ${machine.dataGaps.join('; ')}`
      : undefined,
  });

  const admission = sealEquipmentAdmission({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: `${machine.id}:browse`,
    revisionId: machineRevisionId(machine.id),
    surface: 'browse',
    status: 'admitted',
    requiredObservationIds: [machineIdentityObsId(machine.id)],
    missingMetrics: [],
    conditions: ['Standalone alternator identity for BOM selection and electrical studies.'],
    reason: 'Manually reviewed alternator identity and preferred rating case for library visibility.',
    reviewedAt: COMPILED_AT,
    reviewedBy: CURATOR_ID,
  });

  return { product, revision, observations, admission };
}

// ---------------------------------------------------------------------------
// Reactor designs (3).
// ---------------------------------------------------------------------------

interface ReactorVendorEntry {
  readonly id: string;
  readonly label: string;
  readonly vendorName: string;
  readonly parentClass: string;
  readonly typicalModuleMWe: number | null;
  readonly nrcDocketId: string | null;
  readonly source: string;
}

const reactorRevisionId = (id: string): string => `${id}:${REVISION_LABEL}`;
const reactorIdentityObsId = (id: string): string => `${id}:identity`;
const reactorModuleObsId = (id: string): string => `${id}:module-electrical-output`;
const REACTOR_SOURCE_ID = 'nrc-adams-docket-reference';

function compileReactorSource(): EquipmentSource {
  // A single governed citation wrapper for the NRC ADAMS docket references that
  // back every reactor identity. Facts-only: no performance value originates here.
  return sealEquipmentSource({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: REACTOR_SOURCE_ID,
    kind: 'regulatory-filing',
    publisher: 'U.S. Nuclear Regulatory Commission (ADAMS)',
    title: 'NRC ADAMS reactor design docket references',
    retrievedAt: COMPILED_AT,
    access: 'public',
    visibility: 'public',
    url: 'https://www.nrc.gov/reading-rm/adams.html',
    notes: 'Docket-level identity references for advanced reactor designs; per-design docket ids on each observation locator.',
  });
}

function compileReactor(vendor: ReactorVendorEntry): {
  product: EquipmentLibraryProduct;
  revision: EquipmentRevision;
  observations: EquipmentObservation[];
  admission: EquipmentAdmission;
} {
  const product = sealEquipmentProduct({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: vendor.id,
    level: 'packaged-system',
    domain: 'nuclear-generation',
    kind: 'nuclear-reactor-module',
    manufacturer: vendor.vendorName,
    family: vendor.parentClass,
    model: vendor.label,
    displayName: vendor.label,
    lifecycleStatus: 'licensed-precommercial',
    tags: ['reactor-vendor-catalog', vendor.parentClass],
    facets: emptyFacets({ energySources: ['nuclear'] }),
    currentRevisionId: reactorRevisionId(vendor.id),
    visibility: 'platform',
    origin: 'oem-publication',
    createdAt: COMPILED_AT,
    updatedAt: COMPILED_AT,
  }) as EquipmentLibraryProduct;

  const docketLocator = vendor.nrcDocketId
    ? `NRC docket ${vendor.nrcDocketId}`
    : 'NRC ADAMS reactor docket reference';
  const identity = sealEquipmentObservation({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: reactorIdentityObsId(vendor.id),
    revisionId: reactorRevisionId(vendor.id),
    metric: 'custom:documented-product-identity',
    value: { kind: 'text', value: vendor.label },
    unit: '1',
    basis: { qualifier: 'nominal', configuration: vendor.parentClass },
    sourceRefs: [{ sourceId: REACTOR_SOURCE_ID, locator: docketLocator }],
    evidenceStatus: 'published',
    confidence: 'medium',
    reviewedAt: COMPILED_AT,
    reviewedBy: CURATOR_ID,
  });
  const observations: EquipmentObservation[] = [identity];
  if (vendor.typicalModuleMWe != null) {
    observations.push(sealEquipmentObservation({
      schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
      id: reactorModuleObsId(vendor.id),
      revisionId: reactorRevisionId(vendor.id),
      metric: 'electrical-output',
      value: { kind: 'scalar', value: vendor.typicalModuleMWe },
      unit: 'MW',
      basis: {
        qualifier: 'typical',
        outputBoundary: 'plant-net',
        grossNetBasis: 'net',
        configuration: `${vendor.parentClass} module`,
        notes: 'Typical per-module electrical rating from the vendor design catalog.',
      },
      sourceRefs: [{ sourceId: REACTOR_SOURCE_ID, locator: docketLocator }],
      evidenceStatus: 'published',
      confidence: 'medium',
      reviewedAt: COMPILED_AT,
      reviewedBy: CURATOR_ID,
    }));
  }

  const revision = sealEquipmentRevision({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: reactorRevisionId(vendor.id),
    productId: vendor.id,
    revisionLabel: REVISION_LABEL,
    variantLabel: vendor.label,
    status: 'reviewed',
    configurationLevel: 'packaged-system',
    frequencyHz: [],
    fuels: ['nuclear'],
    duties: ['baseload'],
    sourceIds: [REACTOR_SOURCE_ID],
    observationIds: observations.map(observation => observation.id),
    compiledAt: COMPILED_AT,
    reviewedAt: COMPILED_AT,
    reviewedBy: CURATOR_ID,
  });

  const admission = sealEquipmentAdmission({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: `${vendor.id}:browse`,
    revisionId: reactorRevisionId(vendor.id),
    surface: 'browse',
    status: 'comparison-only',
    requiredObservationIds: [reactorIdentityObsId(vendor.id)],
    missingMetrics: [],
    conditions: [
      'Licensed/precommercial reactor design; visible for research comparison only.',
      'Not admitted for project screening or simulation.',
    ],
    reason: 'Documented advanced-reactor identity is visible for research comparison; commercial maturity is not yet sufficient for project screening.',
    reviewedAt: COMPILED_AT,
    reviewedBy: CURATOR_ID,
  });

  return { product, revision, observations, admission };
}

// ---------------------------------------------------------------------------
// Generation & Impact v1 envelopes (3) — permanent screening admissions with a
// digest-frozen compatibility snapshot.
// ---------------------------------------------------------------------------

const V1_ADAPTER_ID = 'generation-equipment-catalog-v1';
const v1RevisionId = (catalogId: string): string => `${catalogId}:${REVISION_LABEL}`;
const v1IdentityObsId = (catalogId: string): string => `${catalogId}:identity`;
const v1OutputObsId = (catalogId: string): string => `${catalogId}:published-rating`;

/** OEM prime-mover product whose real sources back each v1 envelope. */
const V1_ENVELOPE_SOURCE_PRODUCT: Readonly<Record<string, string>> = {
  'ge-7ha01-1x1-cc-60hz': 'ge-7ha01-1x1-combined-cycle',
  'siemens-scc6-5000f-1x1-60hz': 'siemens-scc6-5000f-1x1',
  'wartsila-24x18v50sg-electrical-island-60hz': 'wartsila-18v50sg',
};

type V1Entry = (typeof GENERATION_EQUIPMENT_CATALOG)[number];

/**
 * The exact solver-facing projection of a v1 envelope. Freezing a digest of
 * this object locks every numerical input the v1 generation solver consumes.
 */
export function generationV1CompatibilitySnapshot(entry: V1Entry): unknown {
  return {
    catalogId: entry.catalogId,
    catalogVersion: entry.catalogVersion,
    dutyHashVersion: entry.dutyHashVersion,
    technology: entry.technology,
    contingencyElement: entry.contingencyElement,
    gasEngineOrTurbineCountPerBlock: entry.gasEngineOrTurbineCountPerBlock,
    publishedRating: entry.publishedRating,
    screeningNormalization: entry.screeningNormalization,
  };
}

export function generationV1CompatibilityDigest(entry: V1Entry): string {
  return hashCanonicalJson(generationV1CompatibilitySnapshot(entry));
}

function v1EnvelopeSourceIds(catalogId: string): readonly string[] {
  const productId = V1_ENVELOPE_SOURCE_PRODUCT[catalogId];
  const product = productId
    ? OEM_GENERATION_SOURCE_REGISTER.products.find(item => item.id === productId)
    : undefined;
  return product ? product.identitySourceIds : [];
}

function compileV1Envelope(entry: V1Entry): {
  revision: EquipmentRevision;
  product: EquipmentLibraryProduct;
  observations: EquipmentObservation[];
  admissions: EquipmentAdmission[];
  legacyIds: LegacyEquipmentIdLink[];
} {
  const sourceIds = v1EnvelopeSourceIds(entry.catalogId);
  const revisionId = v1RevisionId(entry.catalogId);

  const product = sealEquipmentProduct({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: entry.catalogId,
    level: 'plant-configuration',
    domain: 'energy-conversion',
    kind: entry.technology === 'combined-cycle-block'
      ? 'combined-cycle-plant'
      : 'power-island',
    manufacturer: entry.manufacturer,
    family: entry.model,
    model: entry.trainConfiguration,
    displayName: `${entry.manufacturer} ${entry.model} — ${entry.trainConfiguration}`,
    lifecycleStatus: 'commercial-current',
    tags: ['generation-equipment-catalog-v1', entry.technology],
    facets: emptyFacets({ energySources: ['natural-gas'] }),
    currentRevisionId: revisionId,
    visibility: 'platform',
    origin: 'planning-baseline',
    createdAt: COMPILED_AT,
    updatedAt: COMPILED_AT,
  }) as EquipmentLibraryProduct;

  const identity = sealEquipmentObservation({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: v1IdentityObsId(entry.catalogId),
    revisionId,
    metric: 'custom:documented-product-identity',
    value: { kind: 'text', value: `${entry.manufacturer} ${entry.model}` },
    unit: '1',
    basis: { qualifier: 'nominal', configuration: entry.trainConfiguration },
    sourceRefs: sourceIds.map(sourceId => ({ sourceId, locator: 'v1 envelope identity' })),
    evidenceStatus: 'published',
    confidence: 'high',
    reviewedAt: COMPILED_AT,
    reviewedBy: CURATOR_ID,
  });
  const output = sealEquipmentObservation({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: v1OutputObsId(entry.catalogId),
    revisionId,
    metric: 'electrical-output',
    value: { kind: 'scalar', value: entry.publishedRating.outputMWPerBlock },
    unit: 'MW',
    basis: {
      qualifier: 'nominal',
      outputBoundary: entry.publishedRating.ratingScope === 'net-plant'
        ? 'plant-net'
        : 'plant-gross',
      grossNetBasis: entry.publishedRating.ratingScope === 'net-plant' ? 'net' : 'gross',
      referenceConditions: entry.publishedRating.referenceConditions,
      notes: 'Published OEM plant rating used by the Generation & Impact v1 screening solver.',
    },
    sourceRefs: sourceIds.map(sourceId => ({ sourceId, locator: 'v1 envelope published rating' })),
    evidenceStatus: 'published',
    confidence: 'high',
    reviewedAt: COMPILED_AT,
    reviewedBy: CURATOR_ID,
  });
  const observations = [identity, output];

  const revision = sealEquipmentRevision({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: revisionId,
    productId: entry.catalogId,
    revisionLabel: REVISION_LABEL,
    variantLabel: entry.trainConfiguration,
    status: 'reviewed',
    configurationLevel: 'plant-configuration',
    frequencyHz: [60],
    fuels: ['natural gas'],
    duties: ['baseload'],
    sourceIds,
    observationIds: observations.map(observation => observation.id),
    compiledAt: COMPILED_AT,
    reviewedAt: COMPILED_AT,
    reviewedBy: CURATOR_ID,
    notes: `Frozen v1 compatibility digest ${generationV1CompatibilityDigest(entry)}.`,
  });

  const browse = sealEquipmentAdmission({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: `${entry.catalogId}:browse`,
    revisionId,
    surface: 'browse',
    status: 'admitted',
    requiredObservationIds: [v1IdentityObsId(entry.catalogId)],
    missingMetrics: [],
    conditions: [`Catalog version ${entry.catalogVersion}.`],
    reason: 'Immutable Generation & Impact v1 envelope preserved for library visibility.',
    reviewedAt: COMPILED_AT,
    reviewedBy: CURATOR_ID,
  });
  const screening = sealEquipmentAdmission({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: `${entry.catalogId}:generation-screening-v1`,
    revisionId,
    surface: 'generation-screening',
    status: 'admitted',
    adapterId: V1_ADAPTER_ID,
    adapterVersion: String(entry.catalogVersion),
    requiredObservationIds: [v1OutputObsId(entry.catalogId)],
    missingMetrics: [],
    conditions: [
      `Frozen v1 compatibility digest ${generationV1CompatibilityDigest(entry)}.`,
      'Numerical inputs are the immutable v1 screeningNormalization; never re-derived here.',
    ],
    reason: 'Permanent Generation & Impact v1 envelope admission with an exact, digest-frozen compatibility snapshot.',
    reviewedAt: COMPILED_AT,
    reviewedBy: CURATOR_ID,
  });

  const legacyIds: LegacyEquipmentIdLink[] = [
    sealLegacyLink({
      namespace: 'generation-screening-catalog',
      legacyId: entry.catalogId,
      productId: entry.catalogId,
      revisionId,
      mapping: 'exact',
      frozenSnapshotDigest: generationV1CompatibilityDigest(entry),
      notes: 'Generation & Impact v1 envelope; frozen compatibility snapshot.',
    }),
  ];

  return { product, revision, observations, admissions: [browse, screening], legacyIds };
}

// ---------------------------------------------------------------------------
// Legacy-id bridge.
// ---------------------------------------------------------------------------

function sealLegacyLink(input: {
  namespace: LegacyEquipmentIdLink['namespace'];
  legacyId: string;
  productId: string;
  revisionId?: string;
  mapping: LegacyEquipmentIdLink['mapping'];
  frozenSnapshotDigest?: string;
  notes?: string;
}): LegacyEquipmentIdLink {
  return sealLegacyEquipmentIdLink({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: `legacy:${input.namespace}:${input.legacyId}`,
    namespace: input.namespace,
    legacyId: input.legacyId,
    productId: input.productId,
    mapping: input.mapping,
    createdAt: COMPILED_AT,
    ...(input.revisionId ? { revisionId: input.revisionId } : {}),
    ...(input.frozenSnapshotDigest ? { frozenSnapshotDigest: input.frozenSnapshotDigest } : {}),
    ...(input.notes ? { notes: input.notes } : {}),
  });
}

// ---------------------------------------------------------------------------
// Bundle assembly.
// ---------------------------------------------------------------------------

export function buildLegacyEquipmentLibraryBundle(): EquipmentLibraryBundle {
  const oemProducts = OEM_GENERATION_SOURCE_REGISTER.products;
  const machines = OEM_GENERATION_SOURCE_REGISTER.electricalMachines;
  const reactors = Object.values(REACTOR_VENDOR_CATALOG) as unknown as ReactorVendorEntry[];
  const envelopes = GENERATION_EQUIPMENT_CATALOG;

  const sources: EquipmentSource[] = [
    ...OEM_GENERATION_SOURCE_REGISTER.sources.map(compileOemSource),
    compileReactorSource(),
    legacyPlanningBaselineSource(),
  ];
  const planningBenchmarks = buildLegacyPlanningBenchmarks();

  const products: EquipmentLibraryProduct[] = [];
  const revisions: EquipmentRevision[] = [];
  const observations: EquipmentObservation[] = [];
  const admissions: EquipmentAdmission[] = [];
  const legacyIds: LegacyEquipmentIdLink[] = [];

  // OEM prime movers.
  for (const product of oemProducts) {
    products.push(compileOemProduct(product));
    revisions.push(compileOemRevision(product));
    observations.push(...compileOemObservations(product));
    admissions.push(compileOemAdmission(product));
    legacyIds.push(sealLegacyLink({
      namespace: 'oem-generation-product',
      legacyId: product.id,
      productId: product.id,
      revisionId: oemRevisionId(product.id),
      mapping: 'exact',
    }));
    // Old supply-chain catalog row id.
    legacyIds.push(sealLegacyLink({
      namespace: 'equipment-catalog',
      legacyId: `catalog-oem-generation-${product.id}`,
      productId: product.id,
      revisionId: oemRevisionId(product.id),
      mapping: 'exact',
      notes: 'Legacy supply-chain EquipmentCatalogEntry id.',
    }));
    for (const perf of product.performanceCases) {
      legacyIds.push(sealLegacyLink({
        namespace: 'oem-generation-performance-case',
        legacyId: perf.id === product.id ? `${product.id}:${perf.id}` : `${product.id}:${perf.id}`,
        productId: product.id,
        revisionId: oemRevisionId(product.id),
        mapping: 'configuration-alias',
      }));
    }
  }

  // Alternators.
  for (const machine of machines) {
    const compiled = compileMachine(machine);
    products.push(compiled.product);
    revisions.push(compiled.revision);
    observations.push(...compiled.observations);
    admissions.push(compiled.admission);
    legacyIds.push(sealLegacyLink({
      namespace: 'oem-electrical-machine',
      legacyId: machine.id,
      productId: machine.id,
      revisionId: machineRevisionId(machine.id),
      mapping: 'exact',
    }));
    for (const ratingCase of machine.ratingCases) {
      legacyIds.push(sealLegacyLink({
        namespace: 'oem-electrical-rating-case',
        legacyId: `${machine.id}:${ratingCase.id}`,
        productId: machine.id,
        revisionId: machineRevisionId(machine.id),
        mapping: 'configuration-alias',
      }));
    }
  }

  // Reactors.
  for (const reactor of reactors) {
    const compiled = compileReactor(reactor);
    products.push(compiled.product);
    revisions.push(compiled.revision);
    observations.push(...compiled.observations);
    admissions.push(compiled.admission);
    legacyIds.push(sealLegacyLink({
      namespace: 'reactor-vendor-catalog',
      legacyId: reactor.id,
      productId: reactor.id,
      revisionId: reactorRevisionId(reactor.id),
      mapping: 'exact',
    }));
  }

  // v1 envelopes.
  for (const entry of envelopes) {
    const compiled = compileV1Envelope(entry);
    products.push(compiled.product);
    revisions.push(compiled.revision);
    observations.push(...compiled.observations);
    admissions.push(...compiled.admissions);
    legacyIds.push(...compiled.legacyIds);
  }

  return sealEquipmentLibraryBundle({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    compiledAt: COMPILED_AT,
    sources,
    products,
    revisions,
    observations,
    admissions,
    marketEvidence: [],
    companyPatches: [],
    legacyIds,
    planningBenchmarks,
  });
}

export const LEGACY_EQUIPMENT_LIBRARY_BUNDLE = buildLegacyEquipmentLibraryBundle();

/** Frozen v1 compatibility digests, keyed by catalogId (the replay lock). */
export const GENERATION_V1_ENVELOPE_DIGESTS: Readonly<Record<string, string>> =
  Object.freeze(
    Object.fromEntries(
      GENERATION_EQUIPMENT_CATALOG.map(entry => [
        entry.catalogId,
        generationV1CompatibilityDigest(entry),
      ]),
    ),
  );
